import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export interface InternationalizedText {
  en: string
  ar: string
  ur: string
  fa: string
}

export interface InternationalizedArray {
  en: string[]
  ar: string[]
  ur: string[]
  fa: string[]
}

export interface RouteSegment {
  from: InternationalizedText
  to: InternationalizedText
  vehicle: InternationalizedText
  date: string
  duration: string
  distance: string
  hotelIdFrom?: string | null  // Hotel ID for the "from" city
  hotelIdTo?: string | null    // Hotel ID for the "to" city
}

export interface Tour {
  id: string
  title: InternationalizedText
  departureDate: string
  returnDate: string
  duration: string
  price: string
  image: string
  route: RouteSegment[]
  highlights: InternationalizedArray
  description: InternationalizedText
  images?: string[]
  memories?: string[]
  deletedAt?: string | null
}

interface ToursData {
  tours: Tour[]
}

// Utility function to get localized text
export function getLocalizedText(text: InternationalizedText | null | undefined, language: string): string {
  if (!text) {
    return ''
  }
  
  switch (language) {
    case 'ar':
      return text.ar || text.en || ''
    case 'ur':
      return text.ur || text.en || ''
    case 'fa':
      return text.fa || text.en || ''
    default:
      return text.en || ''
  }
}

// Utility function to get localized array
export function getLocalizedArray(array: InternationalizedArray | null | undefined, language: string): string[] {
  if (!array) {
    return []
  }
  
  switch (language) {
    case 'ar':
      return array.ar || array.en || []
    case 'ur':
      return array.ur || array.en || []
    case 'fa':
      return array.fa || array.en || []
    default:
      return array.en || []
  }
}

// Utility function to convert numbers to native numerals based on language
export function getLocalizedNumber(number: string | number, language: string): string {
  const numStr = number.toString()
  
  // Define numeral mappings
  const arabicNumerals = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] // Eastern Arabic/Persian
  const westernNumerals = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
  
  // Convert to appropriate numeral system
  if (language === 'ar' || language === 'ur' || language === 'fa') {
    return numStr.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)])
  }
  
  return numStr // Return as-is for English
}

export function useTours() {
  const [tours, setTours] = useState<Tour[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isAdmin } = useAuth()

  const fetchTours = async () => {
    try {
      setLoading(true)
      
      // Build query - filter out deleted tours for non-admin users
      let query = supabase
        .from('tours')
        .select('*')
        .order('departure_date', { ascending: true })
      
      // Filter out deleted tours for non-admin users at database level
      if (!isAdmin) {
        query = query.is('deleted_at', null)
      }

      const { data: supabaseData, error: supabaseError } = await query

      if (supabaseError) {
        console.warn('Supabase fetch failed, falling back to JSON:', supabaseError)
     
          throw new Error('Failed to fetch tours data from both Supabase')
      } else {
        // Transform Supabase data to match our Tour interface
        const transformedTours: Tour[] = supabaseData.map((row) => ({
          id: row.id,
          title: {
            en: row.title_en || '',
            ar: row.title_ar || '',
            ur: row.title_ur || '',
            fa: row.title_fa || ''
          },
          departureDate: row.departure_date,
          returnDate: row.return_date,
          duration: row.duration || '',
          price: row.price || '',
          image: row.image || '',
          route: row.route || [],
          highlights: {
            en: row.highlights_en || [],
            ar: row.highlights_ar || [],
            ur: row.highlights_ur || [],
            fa: row.highlights_fa || []
          },
          description: {
            en: row.description_en || '',
            ar: row.description_ar || '',
            ur: row.description_ur || '',
            fa: row.description_fa || ''
          },
          images: row.images || [],
          memories: row.memories || [],
          deletedAt: row.deleted_at
        }))
        
        // Additional filter as safety measure (though database should already filter)
        const filteredTours = isAdmin 
          ? transformedTours // Admin sees all tours (including deleted)
          : transformedTours.filter(tour => !tour.deletedAt) // Non-admin sees only non-deleted tours
        
        
        setTours(filteredTours)
      }
      
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching tours:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTours()
  }, [])

  return { tours, loading, error, refetch: fetchTours }
}
